import { useEffect, useMemo, useRef, useState } from 'react';
import { listarEquipe } from '../acesso';
import type { Eu, MembroDaEquipe } from '../acesso';
import { Icone } from '../design/Icone';
import type { NomeDoIcone } from '../design/Icone';
import { listarProdutos } from '../produtos';
import type { ProdutoComercial } from '../produtos';
import { TODOS_OS_ITENS } from './navegacao';

/**
 * TELA 07 — BUSCA GLOBAL. "É uma busca, não um assistente."
 *
 * Overlay central de 680px aberto por Ctrl K ou pela topbar, navegável só com teclado.
 * Encontra, de verdade: as ÁREAS do sistema que a pessoa pode abrir, os PRODUTOS (nome, código,
 * EAN ou fabricante — a mesma busca da tela Produtos) e as PESSOAS da equipe (nome ou código).
 * Cada grupo só aparece para quem tem a permissão dele, e o servidor confere de novo. Nunca
 * devolve um resultado de exemplo.
 *
 * 🚨 O resultado respeita perfil e contexto no SERVIDOR — o balconista em contagem de Giro não
 * usa a busca como atalho para ver o estoque teórico (ele não tem esta busca).
 */

type Achado = {
  chave: string;
  icone: NomeDoIcone;
  rotulo: string;
  detalhe?: string | undefined;
  tipo: string;
  destino: string;
};

export function BuscaGlobal({ aoFechar, eu }: { aoFechar: () => void; eu?: Eu | null }) {
  const [texto, setTexto] = useState('');
  const [marcado, setMarcado] = useState(0);
  const [produtos, setProdutos] = useState<readonly ProdutoComercial[]>([]);
  const [buscandoProdutos, setBuscandoProdutos] = useState(false);
  const [equipe, setEquipe] = useState<readonly MembroDaEquipe[] | null>(null);
  const campo = useRef<HTMLInputElement>(null);
  useEffect(() => campo.current?.focus(), []);

  const pode = (c?: string) => c === undefined || (eu?.capacidades.includes(c) ?? false);
  const veProdutos = pode('produtos.consultar');
  const veEquipe = pode('equipe.ver');

  // a equipe vem uma vez (é pequena); o produto é buscado no servidor enquanto digita
  useEffect(() => {
    if (!veEquipe) return;
    let vivo = true;
    void listarEquipe().then((r) => vivo && r.ok && setEquipe(r.dados.pessoas));
    return () => {
      vivo = false;
    };
  }, [veEquipe]);
  useEffect(() => {
    const t = texto.trim();
    if (!veProdutos || t.length < 2) {
      setProdutos([]);
      setBuscandoProdutos(false);
      return;
    }
    let vivo = true;
    setBuscandoProdutos(true);
    const espera = window.setTimeout(() => {
      void listarProdutos({ filtro: 'todos', busca: t, grupo: '', pagina: 1 }).then((r) => {
        if (!vivo) return;
        setBuscandoProdutos(false);
        setProdutos(r.ok ? r.dados.itens.slice(0, 6) : []);
      });
    }, 250);
    return () => {
      vivo = false;
      window.clearTimeout(espera);
    };
  }, [texto, veProdutos]);

  const achados = useMemo<readonly Achado[]>(() => {
    const t = normalizar(texto);
    const caps = eu?.capacidades ?? [];
    const areas = TODOS_OS_ITENS.filter((i) => !i.capacidade || caps.includes(i.capacidade))
      .filter((i) => !t || normalizar(i.rotulo + ' ' + i.descricao).includes(t))
      .map<Achado>((i) => ({
        chave: `a-${i.rota}`,
        icone: i.icone,
        rotulo: `Abrir ${i.rotulo}`,
        tipo: 'área',
        destino: `#/${i.rota}`,
      }));
    if (!t) return areas;
    const pessoas = (equipe ?? [])
      .filter((p) => normalizar(`${p.nome} ${p.login ?? ''}`).includes(t))
      .slice(0, 5)
      .map<Achado>((p) => ({
        chave: `p-${p.personId}`,
        icone: 'user',
        rotulo: p.nome,
        detalhe: p.login ? `código ${p.login}` : undefined,
        tipo: 'pessoa',
        destino: p.accountId ? `#/perfil?pessoa=${p.accountId}` : '#/equipe',
      }));
    const itens = produtos.map<Achado>((p) => ({
      chave: `r-${p.id}`,
      icone: 'box',
      rotulo: p.descricao,
      detalhe: [p.codigo, p.ean, p.fabricante].filter(Boolean).join(' · ') || undefined,
      tipo: 'produto',
      destino: `#/produtos?produto=${p.id}`,
    }));
    return [...itens, ...pessoas, ...areas.slice(0, 6)];
  }, [texto, produtos, equipe, eu]);

  useEffect(() => setMarcado(0), [texto]);

  const abrir = (destino: string) => {
    window.location.hash = destino;
    aoFechar();
  };

  const teclas = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMarcado((m) => Math.min(m + 1, achados.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMarcado((m) => Math.max(m - 1, 0));
    } else if (e.key === 'Enter') {
      const alvo = achados[marcado];
      if (alvo) abrir(alvo.destino);
    }
  };

  const grupos: readonly [string, string][] = [
    ['produto', 'PRODUTOS'],
    ['pessoa', 'PESSOAS'],
    ['área', texto ? 'ÁREAS' : 'AÇÕES'],
  ];
  const oQueBusca = [veProdutos && 'produto (nome, código, EAN)', veEquipe && 'pessoa', 'tela']
    .filter(Boolean)
    .join(', ');

  return (
    <div className="sh-busca-veu" onClick={aoFechar} role="presentation">
      <div
        className="sh-busca-caixa"
        role="dialog"
        aria-label="Busca global"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={teclas}
      >
        <div className="sh-busca-linha">
          <Icone nome="search" tamanho={18} traco={1.6} />
          <input
            ref={campo}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder={veProdutos ? 'Buscar produto, EAN, pessoa ou tela…' : 'Buscar tela…'}
            aria-label="Buscar"
            role="combobox"
            aria-expanded={achados.length > 0}
            aria-controls="sh-busca-lista"
            aria-activedescendant={
              achados[marcado] ? `sh-busca-${achados[marcado].chave}` : undefined
            }
          />
          <span className="sh-atalho">ESC</span>
        </div>

        {achados.length > 0 ? (
          <ul className="sh-busca-lista" id="sh-busca-lista" role="listbox">
            {grupos.map(([tipo, titulo]) => {
              const doGrupo = achados.filter((a) => a.tipo === tipo);
              if (!doGrupo.length) return null;
              return (
                <li key={tipo} role="presentation">
                  <div className="sh-busca-secao">
                    {titulo} · {doGrupo.length}
                  </div>
                  <ul role="presentation">
                    {doGrupo.map((a) => {
                      const n = achados.indexOf(a);
                      return (
                        <li
                          key={a.chave}
                          id={`sh-busca-${a.chave}`}
                          role="option"
                          aria-selected={n === marcado}
                          className={`sh-busca-item${n === marcado ? ' sh-busca-item-marcado' : ''}`}
                          onMouseEnter={() => setMarcado(n)}
                          onClick={() => abrir(a.destino)}
                        >
                          <Icone nome={a.icone} tamanho={17} traco={1.5} />
                          <span className="sh-busca-item-rotulo">
                            {a.rotulo}
                            {a.detalhe && <em className="sh-busca-item-detalhe">{a.detalhe}</em>}
                          </span>
                          <span className="sh-busca-item-tipo">{a.tipo}</span>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
            {buscandoProdutos && <li className="sh-busca-secao">Buscando produtos…</li>}
          </ul>
        ) : (
          <div className="sh-busca-nada">
            <span className="sh-busca-nada-icone" aria-hidden="true">
              <Icone nome="search" tamanho={17} traco={1.5} />
            </span>
            <div className="sh-busca-nada-titulo">
              {buscandoProdutos ? 'Buscando…' : `Nada encontrado para “${texto}”`}
            </div>
            <div className="sh-busca-nada-texto">A busca encontra {oQueBusca}.</div>
          </div>
        )}

        <div className="sh-busca-rodape">
          <span>
            <kbd>↑↓</kbd> navegar
          </span>
          <span>
            <kbd>↵</kbd> abrir
          </span>
          <span>
            <kbd>ESC</kbd> fechar
          </span>
        </div>
      </div>
    </div>
  );
}

function normalizar(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}
